import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AuthApiError } from '../api';
import { useAuth } from '../context';

export interface LoginPageProps {
  /** Where to send the browser after a successful login. Defaults to `/`. */
  redirectTo?: string;
}

export function LoginPage({ redirectTo }: LoginPageProps): JSX.Element {
  const { login, status } = useAuth();
  const navigate = useNavigate();
  // `RequireAuth` stashes where the member was headed before being bounced here.
  const from = (useLocation().state as { from?: string } | null)?.from;
  const target = redirectTo ?? from ?? '/';
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Already signed in (including the offline last-known-user fallback) — go to the app.
  // Without this, a member whose session expired right before a flight could get stranded
  // on a login form that can't be submitted offline.
  if (status === 'authenticated') {
    return <Navigate to={target} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(email, password);
      void navigate(target, { replace: true });
    } catch (err) {
      if (err instanceof AuthApiError && err.code === 'too_many_attempts') {
        setError('Too many attempts. Try again in a few minutes.');
      } else if (err instanceof AuthApiError && err.code === 'invalid_credentials') {
        setError('Wrong email or password.');
      } else {
        setError('Something went wrong. Try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-bg px-4">
      <form
        onSubmit={(e) => void handleSubmit(e)}
        className="w-full max-w-sm space-y-4 rounded-lg border border-line bg-surface p-6 shadow-sm lg:max-w-md lg:p-8"
      >
        <h1 className="font-display text-xl tracking-tight text-fg">Sign in</h1>

        <label className="block text-sm">
          <span className="mb-1 block text-fg-2">Email</span>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded border border-line bg-bg px-3 py-2 text-fg"
          />
        </label>

        <label className="block text-sm">
          <span className="mb-1 block text-fg-2">Password</span>
          <input
            type="password"
            required
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded border border-line bg-bg px-3 py-2 text-fg"
          />
        </label>

        {error && <p className="text-sm text-danger">{error}</p>}

        <button
          type="submit"
          disabled={submitting}
          className="w-full rounded bg-inverse px-3 py-2 font-medium text-inverse-fg disabled:opacity-50"
        >
          {submitting ? 'Signing in…' : 'Sign in'}
        </button>
      </form>
    </div>
  );
}
